import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import Table from "../../../assets/components/Table/Table";
import {
    LicenseTable,
    Permissions,
    renderSubComponentOneLicenseTable,
} from "../../../models/models";
import LicenseKeysSubRow from "../LicenseKeysSubRow/LicenseKeysSubRow";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { LicenseKeysModel, LicenseList, OneLicense } from "../model";
import moment from "moment";
import CopyToClipboard from "../../../assets/components/CopyToClipboard/CopyToClipboard";
import ImageSprite from "../../../assets/components/ImageSprite/ImageSprite";
import {
    NameStatusLicense,
    detectCurrentNameStatus,
    getStatusData,
} from "../helpers";
import Checkbox from "../../../assets/components/Checkbox/Checkbox";
import { hasPermission } from "../../../assets/helpers/helpers";
import { useAppDispatch, useAppSelector } from "../../../assets/hooks/hooks";
import { licenseSliceActions } from "../../../redux/license-slice";
import ArrowSprite from "../../../assets/components/Table/Img/arrow_sprite.svg";
import { useDeleteLicenseMutation } from "../../../redux/RTKQuery/api";
import { useNotification } from "../../../assets/hooks/useNotification";
import { actionText } from "../../../text";
import RemoveButton from "../../../assets/components/RemoveButton/RemoveButton";
import ButtonArrow from "../../../assets/components/ButtonArrow/ButtonArrow";
import { SearchBarDataHandler } from "../../../assets/components/SearchBar/SearchBar";
import TableColumnVisibilityMenu from "../../../assets/components/Table/TableColumnVisibilityMenu";

const RowItemStatus: FC<{ status: number }> = ({ status }) => {
    const data = getStatusData(status);
    return <span className={`status_color ${data?.color}`}>{data?.name}</span>;
};

type Removed = {
    licenseId: number;
};

const RemovedPost: FC<Removed> = ({ licenseId }) => {
    const [deleteLicense] = useDeleteLicenseMutation({
        fixedCacheKey: "delete_actual_license",
    });
    const { AttentionMessageBox } = useNotification();
    const dispatch = useAppDispatch();

    const deleteHandler = () =>
        AttentionMessageBox({
            textContent: actionText("delete", "licence"),
        }).then((value) => {
            if (value === "yes") {
                deleteLicense([licenseId]);
                dispatch(licenseSliceActions.removeCandidate(licenseId));
            }
        });

    return (
        <RemoveButton
            position="top_center"
            img="top"
            onClick={() => deleteHandler()}
        />
    );
};

type RowItemCheckboxProps = {
    original: OneLicense;
    accessorKey: string;
};

const RowItemCheckbox: FC<RowItemCheckboxProps> = ({
    original,
    accessorKey,
}) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.initializeApp);

    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.LICENSE_DELETE,
    );

    const candidates = useAppSelector(
        (state) => state.license.candidatesForRemove,
    );

    const idLicense = original.license.id;
    const isCandidateForRemove = candidates.includes(idLicense);
    const [stateCheckbox, setStateCheckbox] =
        useState<boolean>(isCandidateForRemove);

    const onChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): React.ChangeEvent<HTMLInputElement> => {
        event.stopPropagation();
        setStateCheckbox(!stateCheckbox);
        !stateCheckbox
            ? dispatch(licenseSliceActions.addCandidate({ id: idLicense }))
            : dispatch(licenseSliceActions.removeCandidate(idLicense));
        return event;
    };

    const isActivated =
        getStatusData(original.license.status)?.name === "Activated";

    useEffect(() => {
        setStateCheckbox(isCandidateForRemove);
    }, [stateCheckbox, candidates, isCandidateForRemove]);

    return (
        <Checkbox
            id={accessorKey}
            name={accessorKey}
            label={original.user.userName}
            checked={isActivated ? false : stateCheckbox}
            value={stateCheckbox.toString()}
            onChange={onChange}
            disabled={
                getStatusData(original.license.status)?.name === "Activated" ||
                !isAccessPermission
            }
            useEllipsis
        />
    );
};

const renderSubComponent: renderSubComponentOneLicenseTable = (props) => {
    return (
        <LicenseKeysSubRow
            {...props.row.original}
            key={props.row.original.license.id}
        />
    );
};

interface Props {
    dataTable: LicenseList;
    onSort: Dispatch<SetStateAction<SearchBarDataHandler>>;
}

const LicenseKeysTable: FC<Props> = ({ dataTable, onSort }) => {
    const [sortNextRebillDate, setSortNextRebillDate] = useState<
        boolean | undefined
    >(undefined);

    const defaultColumnVisibility: VisibilityState = {
        // All columns visible by default
    };

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        defaultColumnVisibility,
    );

    const handleResetColumnVisibility = () => {
        setColumnVisibility(defaultColumnVisibility);
    };

    const handleSort = (state: boolean | undefined) => {
        let newState = state;
        if (state === undefined) newState = true;
        if (state === true) newState = false;
        if (state === false) newState = undefined;

        setSortNextRebillDate(newState);

        onSort((prevData) => {
            return {
                ...prevData,
                sortNextRebillDate: newState,
            };
        });
    };

    const columns: ColumnDef<OneLicense>[] = [
        {
            id: "user",
            header: "User",
            accessorFn: ({ user }) => user.role,
            size: 180,
            cell: ({ row }) => {
                return (
                    <RowItemCheckbox
                        accessorKey={LicenseKeysModel.user.UserName}
                        original={row.original}
                    />
                );
            },
        },
        {
            id: "productName",
            header: "Product Name",
            accessorFn: ({ product }) => product.title,
            size: 180,
        },
        {
            id: "dateCreated",
            header: "Date Created",
            accessorFn: ({ license }) => {
                return moment.unix(license.creationDate).format("LLL");
            },
            size: 160,
        },
        {
            id: "lastRebillDate",
            header: () => <span>Last Rebill date</span>,
            // <ButtonArrow
            //     text="Next Rebill date"
            //     state={sortNextRebillDate}
            //     onClick={() => handleSort(sortNextRebillDate)}
            // />
            // ),
            accessorKey: "lastRebillDate",
            size: 160,
            accessorFn: ({ license }) => {
                if (license.lastRebillDate === 0) {
                    return;
                }
                return moment.unix(license.lastRebillDate).format("LLL");
            },
        },
        {
            header: () => (
                <ButtonArrow
                    text="Next Rebill date"
                    state={sortNextRebillDate}
                    onClick={() => handleSort(sortNextRebillDate)}
                />
            ),
            accessorKey: "nextRebillDate",
            size: 160,
            accessorFn: ({ license }) => {
                if (license.nextRebillDate === 0) {
                    return;
                }
                return moment.unix(license.nextRebillDate).format("LLL");
            },
        },
        {
            header: "License Key",
            accessorFn: ({ license }) => license.key,
            size: 200,
            cell: ({ row }) => (
                <CopyToClipboard text={row.original.license.key} />
            ),
        },
        {
            header: "Status",
            accessorFn: ({ license }) => license.status,
            size: 120,
            minSize: 100,
            cell: ({ row }) => (
                <RowItemStatus status={row.original.license.status} />
            ),
        },
        {
            id: "removed",
            header: () => null,
            size: 30,
            cell: ({ row }) => {
                return (
                    detectCurrentNameStatus(row.original.license.status, [
                        NameStatusLicense.NOT_ACTIVATED,
                        NameStatusLicense.DEACTIVATED,
                        NameStatusLicense.EXPIRED,
                    ]) && <RemovedPost licenseId={row.original.license.id} />
                );
            },
        },
        {
            id: "expander",
            header: () => null,
            size: 30,
            cell: ({ row }) => {
                const arrow = row.getIsExpanded() ? "arrowUp" : "arrowDown";
                return (
                    <button
                        className="edit_button"
                        {...{
                            onClick: () => row.getToggleExpandedHandler(),
                            style: { cursor: "pointer" },
                        }}
                    >
                        <ImageSprite iconSprite={`${ArrowSprite}#${arrow}`} />
                    </button>
                );
            },
        },
    ];

    return (
        <Table<LicenseTable>
            className="key_checker_table"
            data={dataTable}
            columns={columns}
            getRowCanExpand={() => true}
            renderSubComponent={renderSubComponent}
            enableColumnVisibility={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            renderTopToolbar={(table) => (
                <TableColumnVisibilityMenu
                    table={table}
                    onReset={handleResetColumnVisibility}
                />
            )}
        />
    );
};

export default LicenseKeysTable;
